import { InfoCircleFilled } from "@ant-design/icons";
import { DataSource, DataSourceType, NetSuiteAppInfo } from "@coeff/api";
import {
  COLORS,
  NON_CONNECTOR_DATA_SOURCE_TYPES,
  SQL_DATASOURCES,
  SQL_FLOW_REUSE_DATASOURCES,
  isOAuthDataSourceConnection,
  getLookerBaseUrl,
  getUserEmail,
} from "@coeff/utils";
import React from "react";
import styled from "styled-components";

import { FlexColumn } from "../common/FlexLayout";
import { Tooltip } from "../common/Tooltip";
import { Typography } from "../common/Typography";

import { SQLConnectionDetails } from "./SQLConnectionDetails";

type Props = {
  dataSource?: DataSource;
  currentUserId?: string;
  hideImportDetails?: boolean;
  hideSharingDetails?: boolean;
  showMaskedCredentials?: boolean;
  netsuiteAppInfo: NetSuiteAppInfo | undefined;
};

const Container = styled(FlexColumn)`
  font-weight: 500;
  color: ${COLORS.black85};

  .title {
    font-weight: 500;
    color: ${COLORS.black45};
  }

  .description {
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .box {
    background: ${COLORS.coeblue11};
    color: ${COLORS.black45};
    border-radius: 5px;
    text-align: center;
    padding: 8px;
    line-height: 1.2;
    font-size: 12px;
  }
`;

export const DataSourceInfoTooltip: React.FC<Props> = ({
  dataSource,
  currentUserId,
  hideImportDetails,
  hideSharingDetails,
  netsuiteAppInfo,
}) => {
  return (
    <>
      {dataSource && dataSource.data_source_type && (
        <Tooltip
          title={
            <div style={{ padding: 10, lineHeight: 1.3, maxWidth: 210 }}>
              <DataSourceInfo
                dataSource={dataSource}
                currentUserId={currentUserId}
                hideImportDetails={hideImportDetails}
                hideSharingDetails={hideSharingDetails}
                netsuiteAppInfo={netsuiteAppInfo}
              />
            </div>
          }
          placement="bottomRight"
          arrowPointAtCenter={true}
          overlayStyle={{ pointerEvents: "none" }}
        >
          <InfoCircleFilled style={{ color: COLORS.black25 }} />
        </Tooltip>
      )}
    </>
  );
};

const PASSWORD_ENTITY = <>&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;</>;

export const DataSourceInfo: React.FC<Props> = ({
  dataSource,
  currentUserId,
  hideImportDetails,
  hideSharingDetails,
  showMaskedCredentials,
  netsuiteAppInfo,
}) => {
  const selectedSchemas = dataSource?.credentials?.selected_schemas?.join(", ");

  const isLookerOAuthConnection =
    dataSource && isOAuthDataSourceConnection({ dataSource, dataImport: undefined });

  return (
    <>
      <Container>
        {dataSource?.data_source_type === "salesforce" && (
          <>
            <div>
              <div className="title">Username</div>
              <div className="description">
                {dataSource?.connection_info?.salesforce_preferred_username ?? "-"}
              </div>
            </div>

            <div>
              <div className="title">Organization ID</div>
              <div className="description">
                {dataSource?.connection_info?.salesforce_organization_id ?? "-"}
              </div>
            </div>

            {dataSource?.ppress_metadata?.is_salesforce_sandbox && (
              <div>
                <div className="title">Mode</div>
                <div>Sandbox</div>
              </div>
            )}
          </>
        )}

        {dataSource &&
          (SQL_DATASOURCES.includes(dataSource.data_source_type) ||
            SQL_FLOW_REUSE_DATASOURCES.includes(dataSource.data_source_type)) &&
          dataSource?.data_source_type !== "bigquery" && (
            <SQLConnectionDetails
              dataSource={dataSource}
              showMaskedCredentials={showMaskedCredentials}
            />
          )}

        {dataSource?.data_source_type === "shopify" && (
          <>
            <div>
              <div className="title">Username</div>
              <div className="description">{dataSource?.connection_info?.shopify_email ?? "-"}</div>
            </div>

            <div>
              <div className="title">Shop Domain</div>
              <div className="description">{dataSource?.ppress_metadata?.shop_url ?? "-"}</div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "quickbooks" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.quickbooks_email ?? "-"}
              </div>
            </div>
            <div>
              <div className="title">Company name</div>
              <div className="description">
                {dataSource?.connection_info?.quickbooks_company_name ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "jira" && (
          <>
            <div>
              <div className="title">Username</div>
              <div className="description">
                {dataSource?.connection_info?.jira_email
                  ? dataSource.connection_info.jira_email
                  : "-"}
              </div>
            </div>
            <div>
              <div className="title">Account ID</div>
              <div className="description">
                {dataSource?.connection_info?.jira_account_id ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "hubspot" && (
          <>
            <div>
              <div className="title">Username</div>
              <div className="description">
                {dataSource?.connection_info?.hubspot_user_email ?? "-"}
              </div>
            </div>
            <div>
              <div className="title">Hub ID</div>
              <div className="description">
                {dataSource?.connection_info?.hubspot_hub_id ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "zendesk" && (
          <>
            <div>
              <div className="title">Username</div>
              <div className="description">{dataSource?.connection_info?.zendesk_email ?? "-"}</div>
            </div>
            <div>
              <div className="title">Account URL</div>
              <div className="description">{dataSource?.ppress_metadata?.subdomain ?? "-"}</div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "stripe" && (
          <>
            <div>
              <div className="title">Business name</div>
              <div className="description">
                {dataSource?.connection_info?.stripe_business_name ?? "-"}
              </div>
            </div>
            <div>
              <div className="title">Account owner</div>
              <div className="description">{dataSource?.connection_info?.stripe_email ?? "-"}</div>
            </div>
            <div>
              <div className="title">Account ID</div>
              <div className="description">{dataSource?.connection_info?.stripe_id ?? "-"}</div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "bigquery" && (
          <>
            <div>
              <div className="title">GCP Project ID</div>
              <div className="description">
                {dataSource.credentials.client_key_json
                  ? dataSource.credentials.client_key_json["project_id"]
                  : dataSource.credentials?.gcloud_project_id}
              </div>
            </div>

            {selectedSchemas && (
              <div>
                <div className="title">Visible Datasets</div>
                <div className="description">
                  {selectedSchemas.slice(0, 100) + (selectedSchemas.length > 100 ? "..." : "")}
                </div>
              </div>
            )}
          </>
        )}

        {dataSource?.data_source_type === "tableau" && (
          <div>
            <div className="title">Account URL</div>
            <div>{dataSource?.credentials?.url?.replace("https://", "")}</div>
            <div className="title">Site Name</div>
            <div>{dataSource?.credentials?.tableau_site_name}</div>
          </div>
        )}

        {dataSource?.data_source_type === "looker" &&
          (isLookerOAuthConnection ? (
            <>
              <div>
                <div className="title">Base URL</div>
                <Typography noWrap>
                  {getLookerBaseUrl(
                    dataSource?.ppress_metadata?.base_url || dataSource?.credentials?.url || ""
                  ) ?? "-"}
                </Typography>
              </div>
              <div>
                <div className="title">Email</div>
                <Typography noWrap>
                  {dataSource?.connection_info?.looker_user_email ?? "-"}
                </Typography>
              </div>
            </>
          ) : (
            <>
              <div>
                <div className="title">Base URL</div>
                <Typography noWrap>
                  {getLookerBaseUrl(dataSource?.credentials?.url || "") ?? "-"}
                </Typography>
              </div>
              <div>
                <div className="title">Client ID</div>
                <Typography noWrap>{dataSource?.credentials?.client_id ?? "_"}</Typography>
              </div>
              <div>
                <div className="title">Client Secret</div>
                <Typography noWrap>{PASSWORD_ENTITY}</Typography>
              </div>
            </>
          ))}

        {dataSource?.data_source_type === DataSourceType.NetflixQuiver && (
          <>
            <div>
              <div className="title">Endpoint</div>
              <Typography noWrap>
                {dataSource?.connection_info?.netflix_quiver_app_name || "-"}
              </Typography>
            </div>
            <div>
              <div className="title">Base URL</div>
              <Typography noWrap>
                {dataSource?.connection_info?.netflix_quiver_base_url?.replace(
                  /^https?:\/\//,
                  ""
                ) || "-"}
              </Typography>
            </div>
            <div>
              <div className="title">Email</div>
              <Typography noWrap>
                {dataSource?.connection_info?.netflix_quiver_user_email || "-"}
              </Typography>
            </div>
          </>
        )}

        {NON_CONNECTOR_DATA_SOURCE_TYPES.includes(
          dataSource?.data_source_type as DataSourceType
        ) && (
          <div>
            <div className="title">Email</div>
            <div>{"-"}</div>
          </div>
        )}

        {["google_analytics", "google_analytics_4"].includes(
          dataSource?.data_source_type ?? ""
        ) && (
          <div>
            <div className="title">Username</div>
            <div className="description">
              {dataSource?.connection_info?.google_analytics_username ?? "-"}
            </div>
          </div>
        )}

        {dataSource?.data_source_type === "facebook_ads" && (
          <div>
            <div className="title">Username</div>
            <div className="description">
              {dataSource?.connection_info?.facebook_ads_user_email ?? "-"}
            </div>
          </div>
        )}

        {dataSource?.data_source_type === "facebook_insights" && (
          <div>
            <div className="title">Username</div>
            <div className="description">
              {dataSource?.connection_info?.facebook_insights_user_email ?? "-"}
            </div>
          </div>
        )}

        {dataSource?.data_source_type === DataSourceType.InstagramInsights && (
          <div>
            <div className="title">Email</div>
            <div className="description">
              {dataSource?.connection_info?.instagram_insights_user_email ?? "-"}
            </div>
          </div>
        )}

        {dataSource?.data_source_type === "pipedrive" && (
          <>
            <div>
              <div className="title">Organization Name</div>
              <div className="description">
                {dataSource?.connection_info?.pipedrive_org_name ?? "-"}
              </div>
            </div>
            <div>
              <div className="title">Organization ID</div>
              <div className="description">
                {dataSource?.connection_info?.pipedrive_company_id ?? "-"}
              </div>
            </div>
            <div>
              <div className="title">User</div>
              <div className="description">
                {dataSource?.connection_info?.pipedrive_user_name ?? "-"} (
                {dataSource?.connection_info?.pipedrive_user_email ?? "-"})
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "mailchimp" && (
          <>
            <div>
              <div className="title">Username (Role)</div>
              <div className="description">
                {/* TODO: create a common method to handle all default "-"s */}
                {dataSource?.connection_info?.mailchimp_user_name ?? "-"} (
                {dataSource?.connection_info?.mailchimp_user_role ?? "-"})
              </div>
            </div>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.mailchimp_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "outreach" && (
          <>
            <div>
              <div className="title">Organization Name</div>
              <div className="description">
                {dataSource?.connection_info?.outreach_organization_name ?? "-"}
              </div>
            </div>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.outreach_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "google_search_console" && (
          <div>
            <div className="title">Email</div>
            <div className="description">
              {dataSource?.connection_info?.google_search_console_user_email ?? "-"}
            </div>
          </div>
        )}

        {dataSource?.data_source_type === "gmail" && (
          <div>
            <div className="title">Email</div>
            <div className="description">
              {dataSource?.connection_info?.gmail_user_email ?? "-"}
            </div>
          </div>
        )}

        {dataSource?.data_source_type === "google_ads" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.google_ads_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "campfire" && (
          <>
            <div>
              <div className="title">Account</div>
              <div className="description">
                {dataSource?.connection_info?.campfire_account_name ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "rillet" && (
          <>
            <div>
              <div className="title">Account</div>
              <div className="description">
                {dataSource?.connection_info?.rillet_account_name ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "chargebee" && (
          <>
            <div>
              <div className="title">Site Name</div>
              <div className="description">{dataSource?.ppress_metadata?.site_name ?? "-"}</div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "linkedin_ads" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.linkedin_ads_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "youtube_analytics" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.youtube_analytics_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "linkedin_pages" && (
          <>
            <div>
              <div className="title">LinkedIn User Name</div>
              <div className="description">
                {dataSource?.connection_info?.linkedin_pages_user_localized_name ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "notion" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">{dataSource?.connection_info?.notion_email ?? "-"}</div>
            </div>
            <div>
              <div className="title">Workspace</div>
              <div className="description">
                {dataSource?.connection_info?.notion_workspace ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "airtable" && (
          <>
            <div>
              <div className="title">User Id</div>
              <div className="description">
                {dataSource?.connection_info?.airtable_user_id ?? "-"}
              </div>
            </div>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.airtable_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "microsoft_ads" && (
          <div>
            <div className="title">Email</div>
            <div className="description">
              {dataSource?.connection_info?.microsoft_ads_user_email}
            </div>
          </div>
        )}

        {dataSource?.data_source_type === "klaviyo" && (
          <div>
            <div className="title">Email</div>
            <div className="description">{dataSource?.connection_info?.klaviyo_user_email}</div>
          </div>
        )}

        {dataSource?.data_source_type === "asana" && (
          <div>
            <div className="title">Email</div>
            <div className="description">{dataSource?.connection_info?.asana_user_email}</div>
          </div>
        )}

        {dataSource?.data_source_type === "xero" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.xero_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "clickup" && (
          <>
            <div>
              <div className="title">User name</div>
              <div className="description">
                {dataSource?.connection_info?.clickup_user_name ?? "-"}
              </div>
            </div>
            <div>
              <div className="title">Workspaces</div>
              <div className="description">
                {dataSource?.connection_info?.clickup_workspaces ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "typeform" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.typeform_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "freshdesk" && (
          <>
            <div>
              <div className="title">Domain name</div>
              <div className="description">
                {dataSource?.connection_info?.freshdesk_account_name ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "paymo" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.paymo_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "harvest" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.harvest_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "razorpay" && (
          <>
            <div>
              <div className="title">Razorpay Key Id</div>
              <div className="description">
                {dataSource?.connection_info?.razorpay_key_id ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "google_calendar" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.google_calendar_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "amazon_seller_central" && (
          <>
            <div>
              <div className="title">Marketplace ID</div>
              <div className="description">
                {dataSource?.connection_info?.amazon_seller_central_marketplace_id ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "trello" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.trello_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "gong" && (
          <>
            <div>
              <div className="title">Account</div>
              <div className="description">
                {dataSource?.connection_info?.gong_account_name ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "calendly" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.calendly_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "smartsheet" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.smartsheet_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "reddit_ads" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.reddit_ads_account_id ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "surveymonkey" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.surveymonkey_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "quora_ads" && (
          <>
            <div>
              <div className="title">Account Name</div>
              <div className="description">
                {dataSource?.connection_info?.quora_ads_user_name ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "podio" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.podio_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "greenhouse" && (
          <>
            <div>
              <div className="title">User Name</div>
              <div className="description">
                {dataSource?.connection_info?.greenhouse_api_key ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "active_campaign" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.active_campaign_account_name ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "basecamp" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.basecamp_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "jotform" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.jotform_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "zohobooks" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.zohobooks_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "gusto" && (
          <>
            <div>
              <div className="title">Company Id</div>
              <div className="description">
                {dataSource?.connection_info?.gusto_company_id ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "intercom" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.intercom_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "etsy" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.etsy_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "pinterest_ads" && (
          <>
            <div>
              <div className="title">User name</div>
              <div className="description">
                {dataSource?.connection_info?.pinterest_ads_user_name ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "coda" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.coda_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "square" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.square_user_business_name ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "zoom" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.zoom_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "amplitude" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.amplitude_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "webflow" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.webflow_user_email ?? "-"}
              </div>
            </div>
            {dataSource?.connection_info?.webflow_user_workspaces_or_sites && (
              <div>
                <div className="title">Workspaces or Sites</div>
                <div className="description">
                  {dataSource?.connection_info?.webflow_user_workspaces_or_sites ?? "-"}
                </div>
              </div>
            )}
          </>
        )}

        {dataSource?.data_source_type === "zoho_crm" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.zoho_crm_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "lever" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.lever_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "bigin" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.bigin_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "salesloft" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.salesloft_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "wordpress" && (
          <>
            <div>
              <div className="title">WordPress Site</div>
              <div className="description">
                {dataSource?.connection_info?.wordpress_site ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "woo_commerce" && (
          <>
            <div>
              <div className="title">WooCommerce Site</div>
              <div className="description">
                {dataSource?.connection_info?.woo_commerce_site ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "bigcommerce" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.bigcommerce_user_email ?? "-"}
              </div>
            </div>
            <div>
              <div className="title">Store Id</div>
              <div className="description">
                {dataSource?.connection_info?.bigcommerce_store_id ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "netsuite" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.netsuite_user_email ?? "-"}
              </div>
            </div>

            <div>
              <div className="title">Account ID</div>
              <div className="description">
                {dataSource?.connection_info?.netsuite_account_id ?? "-"}
              </div>
            </div>

            <div>
              <div className="title">External URL</div>
              <div>
                <Tooltip title={netsuiteAppInfo?.external_url}>
                  <Typography noWrap>{netsuiteAppInfo?.external_url}</Typography>
                </Tooltip>
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "netsuite_crx" && (
          <>
            <div>
              <div className="title">Account ID</div>
              <div className="description">
                {dataSource?.connection_info?.netsuite_account_id ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "smartlead" && (
          <>
            <div>
              <div className="title">API Key</div>
              <div className="description">
                {dataSource?.connection_info?.smartlead_api_key ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "affinity" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.affinity_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "shiprocket" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.shiprocket_api_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "rippling" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.rippling_work_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "apollo" && (
          <>
            <div>
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.apollo_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "aws_billing" && (
          <>
            <div>
              <div className="title">AWS Access Key ID</div>
              <div className="description">
                {dataSource?.connection_info?.aws_access_key_id ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "sage" && (
          <>
            <div className="row">
              <div className="title">Company ID</div>
              <div className="description">
                {dataSource?.connection_info?.sage_company_id ?? "-"}
              </div>
            </div>
            <div className="row">
              <div className="title">User ID</div>
              <div className="description">{dataSource?.connection_info?.sage_user_id ?? "-"}</div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "google_classroom" && (
          <>
            <div className="row">
              <div className="title">Email</div>
              <div className="description">
                {dataSource?.connection_info?.google_classroom_user_email ?? "-"}
              </div>
            </div>
          </>
        )}

        {dataSource?.data_source_type === "any_api_agent" && (
          <>
            {Object.entries(dataSource?.ppress_metadata?.connection_info ?? {}).map(
              ([key, value]) => (
                <div key={key}>
                  <div className="title">
                    {(key || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                  </div>
                  <div className="description">{value}</div>
                </div>
              )
            )}
          </>
        )}

        {!hideImportDetails && (
          <div>
            <div className="title">Imports using this connection</div>
            <div className="description">
              {(dataSource?.data_source_stats?.num_data_imports ?? 0) +
                (dataSource?.data_source_stats?.num_shared_data_imports ?? 0)}
            </div>
          </div>
        )}

        {!hideSharingDetails &&
          dataSource?.is_shared_with_domain &&
          dataSource?.user &&
          currentUserId &&
          dataSource?.user?.user_id !== currentUserId && (
            <div>
              <div className="box">
                This connection is shared by {getUserEmail(dataSource?.user)}. Please contact them
                directly for any changes.
              </div>
            </div>
          )}
      </Container>
    </>
  );
};
